interface CachedPage {
  fileId: string;
  pageNumber: number;
  imageUrl: string;
  image: HTMLImageElement;
  lastAccessed: number;
}

class PDFPageCacheService {
  private pageCache: Map<string, CachedPage> = new Map();
  private readonly MAX_PAGES = 200;

  getCacheKey(fileId: string, pageNumber: number): string {
    return `${fileId}-${pageNumber}`;
  }

  getPage(fileId: string, pageNumber: number): HTMLImageElement | null {
    const key = this.getCacheKey(fileId, pageNumber);
    const cached = this.pageCache.get(key);

    if (cached) {
      cached.lastAccessed = Date.now();
      return cached.image;
    }

    return null;
  }

  setPage(fileId: string, pageNumber: number, image: HTMLImageElement): void {
    const key = this.getCacheKey(fileId, pageNumber);

    // Enforce cache limit
    if (this.pageCache.size >= this.MAX_PAGES) {
      this.evictOldest();
    }

    this.pageCache.set(key, {
      fileId,
      pageNumber,
      imageUrl: image.src,
      image,
      lastAccessed: Date.now(),
    });
  }

  private evictOldest(): void {
    let oldest: [string, CachedPage] | null = null;

    for (const entry of this.pageCache.entries()) {
      if (!oldest || entry[1].lastAccessed < oldest[1].lastAccessed) {
        oldest = entry;
      }
    }

    if (oldest) {
      // Clean up blob URL if needed
      if (oldest[1].imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(oldest[1].imageUrl);
      }
      this.pageCache.delete(oldest[0]);
    }
  }

  clearForFile(fileId: string): void {
    const keysToDelete: string[] = [];

    for (const [key, cached] of this.pageCache.entries()) {
      if (cached.fileId === fileId) {
        if (cached.imageUrl.startsWith("blob:")) {
          URL.revokeObjectURL(cached.imageUrl);
        }
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.pageCache.delete(key));
  }

  clearAll(): void {
    this.pageCache.forEach((cached) => {
      if (cached.imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(cached.imageUrl);
      }
    });
    this.pageCache.clear();
  }

  // Debug methods
  getCacheStats(): { size: number; fileIds: string[] } {
    const fileIds = Array.from(new Set(Array.from(this.pageCache.values()).map((cached) => cached.fileId)));
    return {
      size: this.pageCache.size,
      fileIds,
    };
  }
}

export const pdfPageCacheService = new PDFPageCacheService();
